import type { ConnectError } from '@connectrpc/connect'
import {
  SubmitterMasterErrorReason,
  SubmitterMasterErrorReasonSchema,
} from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type { TFunction } from 'i18next'

import {
  type AppErrorInfoReasonEntry,
  type AppErrorInfoResolution,
  resolveAppErrorInfoMessage,
} from '~/libs/appErrorInfo'

/**
 * `SubmitterMasterErrorReason` (docs/adr/structured-app-error-typed-reason.md
 * decision 1/2, backend `occtomaster` domain) → i18n key + field routing
 * registry for the SubmitterMaster Create/Update modals (mirrors
 * BgMaster/models/serverError.ts, decision E). See that file's
 * `BG_MASTER_REASON_KEYS` godoc for the `Record<Enum,
 * AppErrorInfoReasonEntry | undefined>` compile-time-coverage contract and
 * the `field` routing rationale (docs/adr/
 * structured-app-error-standard-errorinfo-envelope.md decision 2).
 */
const SUBMITTER_MASTER_REASON_KEYS: Record<
  SubmitterMasterErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [SubmitterMasterErrorReason.UNSPECIFIED]: undefined,
  [SubmitterMasterErrorReason.SUBMITTER_CODE_INVALID_FORMAT]: {
    key: 'page:adminOcctoMaster.serverError.reason.submitterCodeInvalidFormat',
    field: 'submitterCode',
  },
  [SubmitterMasterErrorReason.BUSINESS_MASTER_NOT_FOUND]: {
    key: 'page:adminOcctoMaster.serverError.reason.businessMasterNotFound',
    field: 'businessMasterId',
  },
  [SubmitterMasterErrorReason.SUBMITTER_CODE_ALREADY_EXISTS]: {
    key: 'page:adminOcctoMaster.serverError.reason.submitterCodeAlreadyExists',
  },
  [SubmitterMasterErrorReason.MEMBER_LICENSE_AREA_MISMATCH]: {
    key: 'page:adminOcctoMaster.serverError.reason.submitterMemberLicenseAreaMismatch',
  },
}

/**
 * `resolveDetailedError` for SubmitterMaster's `useCrudModalForm` (Create /
 * Update). Resolves the `google.rpc.ErrorInfo` Connect error detail, if
 * any, against `SUBMITTER_MASTER_REASON_KEYS` via the
 * `SubmitterMasterErrorReasonSchema` reflection lookup.
 */
export function resolveSubmitterMasterDetailedError(
  err: ConnectError,
  t: TFunction,
): AppErrorInfoResolution | undefined {
  return resolveAppErrorInfoMessage(
    err,
    SubmitterMasterErrorReasonSchema,
    SUBMITTER_MASTER_REASON_KEYS,
    t,
  )
}
